import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  NgZone,
  OnDestroy,
  Output,
  ViewChild
} from '@angular/core';
import { GestureMode, FingerMode, RecordedAction, Step } from '../../Models/recorded-action-model';
import { MobileSessionService } from '../../Services/mobile-session.service';
import { Literals } from '../../literals';
import { getBrand } from 'src/environments/brand';

declare class ResizeObserver {
  constructor(cb: ResizeObserverCallback);
  observe(target: Element): void;
  unobserve(target: Element): void;
  disconnect(): void;
}
type ResizeObserverCallback = (entries: ResizeObserverEntry[], observer: ResizeObserver) => void;
interface ResizeObserverEntry { readonly target: Element; readonly contentRect: DOMRectReadOnly; }

export interface ContextMenuResult {
  action: 'get-text' | 'verify-text' | 'assert-text' | 'verify-text-contains';
  x: number;
  y: number;
  result: string;
  element: RecordedAction['element'];
}

@Component({
  selector: 'ze-device-screen',
  templateUrl: './device-screen.component.html',
  styleUrls: ['./device-screen.component.scss']
})
export class DeviceScreenComponent implements AfterViewInit, OnDestroy {
  brand = getBrand();

  @ViewChild('screenCanvas', { static: true }) canvasRef!: ElementRef<HTMLCanvasElement>;
  @ViewChild('overlayCanvas', { static: true }) overlayRef!: ElementRef<HTMLCanvasElement>;
  @ViewChild('screenImage', { static: true }) imageRef!: ElementRef<HTMLImageElement>;
  @ViewChild('ctxMenu', { static: false }) ctxMenuRef!: ElementRef<HTMLDivElement>;


  @Input() step: Step = 'upload';
  @Input() fingerMode: FingerMode = '1';
  @Input() selectedMode: GestureMode = 'tap';
  @Input() isRecording = false;
  @Input() uploadedAppName: string | null = null;
  @Input() isFetchingLocator = false;
  @Input() isModalOpen = false;

  @Output() actionCaptured = new EventEmitter<RecordedAction>();
  @Output() locatorFetchingChange = new EventEmitter<boolean>();
  @Output() connected = new EventEmitter<void>();
  @Output() disconnected = new EventEmitter<void>();
  @Output() streamError = new EventEmitter<string>();
  @Output() stepChange = new EventEmitter<Step>();
  @Output() contextMenuAction = new EventEmitter<ContextMenuResult>();
  @Output() endSession = new EventEmitter<void>();
  @Output() typeRequested = new EventEmitter<{
    coords: { x: number; y: number; realX: number; realY: number };
    element: RecordedAction['element'];
  }>();

  isConnected = false;
  isRefreshing = false;
  isRecordingSlider = false;

  /**
   * Explicit pixel height for the screen-wrapper, derived from the real device aspect ratio.
   * We use a fixed width (332px = 360px frame minus 2×14px border) and scale height from that.
   * Default 9:19.5 ≈ 1080×2280 so the shell looks right before the first frame arrives.
   */
  screenHeight = '700px';

  // ── Right-click context menu state ────────────────────────────────────────
  showContextMenu = false;
  contextMenuVisible = false;   // hidden until position is measured & corrected
  contextMenuX = 0;
  contextMenuY = 0;
  contextMenuRealX = 0;
  contextMenuRealY = 0;

  private socket!: WebSocket;
  private pendingBlob: Blob | null = null;
  private isBitmapRendering = false;
  private actionCounter = 0;
  private swipeStart: { x: number; y: number; realX: number; realY: number } | null = null;
  private longPressTimer: any = null;
  private isLongPress = false;
  private overlayTimer: any = null;
  private isRightClickPending = false;  // blocks tap from firing on right-click

  private viewReady = false;
  private pendingConnect = false;
  readonly gestureLiterals = Literals;
  private sliderElement: RecordedAction['element'] = null;
  private sliderBounds: { x1: number; y1: number; x2: number; y2: number } | null = null;

  // ── Pre-tap snapshot ──────────────────────────────────────────────────────────
  // We fetch the page source at pointerdown (finger-touch moment) instead of
  // waiting for the 300ms double-tap timer to expire.
  //
  // WHY: Dropdowns, pickers, and bottom-sheets open/close within ~100ms.
  // By the time the 300ms timer fires the list is already gone and the source
  // no longer contains the option the user tapped. Snapshotting at pointerdown
  // captures the screen exactly as the user sees it at the moment of touch.
  private preTapSourceSnapshot: string | null = null;
  private preTapSnapshotCoords: { realX: number; realY: number } | null = null;
  private preTapSourcePromise: Promise<string> | null = null;

  constructor(
    private ngZone: NgZone,
    private session: MobileSessionService
  ) { }

  // ── Lifecycle ─────────────────────────────────────────────────────────────────

  ngAfterViewInit() {
    this.viewReady = true;
    setTimeout(() => {
      const canvas = this.canvasRef.nativeElement;
      const overlay = this.overlayRef.nativeElement;
      const w = canvas.clientWidth;
      const h = canvas.clientHeight;
      if (w > 0 && h > 0) {
        canvas.width = w; canvas.height = h;
        overlay.width = w; overlay.height = h;
      }
      if (this.pendingConnect) {
        this.pendingConnect = false;
        this._connectWebSocket();
      }
    }, 0);
  }

  ngOnDestroy() {
    if (this.socket) this.socket.close(1000, 'Session ended');
  }

  // ── Public API ────────────────────────────────────────────────────────────────

  connectWebSocket(): void {
    if (!this.viewReady) { this.pendingConnect = true; return; }
    this._connectWebSocket();
  }

  closeSocket(): void {
    if (this.socket) this.socket.close(1000, 'Session ended');
    this.isConnected = false;
    this.isBitmapRendering = false;
    this.pendingBlob = null;

    // ── Clear the canvas so stale stream frame is not shown after session ends ──
    const canvas = this.canvasRef.nativeElement;
    const overlay = this.overlayRef.nativeElement;
    const ctx = canvas.getContext('2d');
    const octx = overlay.getContext('2d');
    if (ctx) ctx.clearRect(0, 0, canvas.width, canvas.height);
    if (octx) octx.clearRect(0, 0, overlay.width, overlay.height);
  }

  /** Force-reconnect the WebSocket stream when it gets stuck on a frame. */
  refreshStream(): void {
    if (this.isRefreshing || !this.session.sessionId) return;
    this.isRefreshing = true;

    // Close existing socket cleanly without clearing the canvas
    // (user can still see the last frame while reconnecting)
    if (this.socket) {
      this.socket.onclose = null;  // suppress the disconnected emit
      this.socket.close(1000, 'Refresh');
    }
    this.isConnected = false;
    this.isBitmapRendering = false;
    this.pendingBlob = null;

    // Reconnect after a short delay so the server has time to release the slot
    setTimeout(() => {
      this._connectWebSocket();
      this.isRefreshing = false;
    }, 600);
  }

  resetActionCounter(): void { this.actionCounter = 0; }

  /** Called by parent after type is confirmed in gesture-controls panel.
   *  element is the PRE-TAP snapshot captured before typing began — so xpaths
   *  reflect the original field state, not the post-type content. */
  recordTypeAction(
    coords: { x: number; y: number; realX: number; realY: number },
    text: string,
    element: RecordedAction['element']
  ): void {
    const id = ++this.actionCounter;
    this.ngZone.run(() => {
      this.actionCaptured.emit({
        id,
        action: Literals.TYPE,
        element,                           // pre-tap snapshot — correct xpaths
        coordinates: { x: coords.realX, y: coords.realY },
        value: text,
        timestamp: new Date().toISOString()
      });
    });
  }

  // ── WebSocket + frame rendering ───────────────────────────────────────────────

  private _connectWebSocket(): void {
    if (!this.session.sessionId) return;
    const wsUrl = this.session.getStreamUrl();
    // console.log('🔌 Connecting WebSocket:', wsUrl);

    this.socket = new WebSocket(wsUrl);
    this.socket.binaryType = 'blob';

    this.socket.onopen = () => this.ngZone.run(() => {
      this.isConnected = true;
      this.stepChange.emit('streaming');
      this.connected.emit();
      setTimeout(() => this.setupCanvasEvents(), 100);
    });

    this.socket.onclose = () => this.ngZone.run(() => {
      this.isConnected = false;
      this.disconnected.emit();
    });

    this.socket.onerror = () => this.ngZone.run(() => {
      this.streamError.emit('Stream connection failed.');
    });

    this.socket.onmessage = (event: MessageEvent) => {
      this.pendingBlob = event.data as Blob;
      if (!this.isBitmapRendering) {
        this.isBitmapRendering = true;
        requestAnimationFrame(() => this.renderFrame());
      }
    };
  }

  private renderFrame(): void {
    const currentBlob = this.pendingBlob;
    this.pendingBlob = null;

    if (!currentBlob) {
      if (this.isConnected) {
        requestAnimationFrame(() => this.renderFrame());
      } else {
        this.isBitmapRendering = false;
      }
      return;
    }

    createImageBitmap(currentBlob)
      .then(bitmap => {
        const canvas = this.canvasRef.nativeElement;
        const ctx = canvas.getContext('2d');

        if (!this.session.deviceResolutionResolved && bitmap.width > 0 && bitmap.height > 0) {
          const scale = bitmap.width <= 600 ? 2 : 1;
          this.session.imageWidth = bitmap.width * scale;
          this.session.imageHeight = bitmap.height * scale;
          this.session.deviceResolutionResolved = true;
          // Compute explicit height from real device aspect ratio.
          // screen-wrapper inner width = 360px frame − 2×14px border = 332px.
          // We derive height from that so the canvas is never stretched.
          this.ngZone.run(() => {
            const innerW = 332;
            this.screenHeight = Math.round(innerW * this.session.imageHeight / this.session.imageWidth) + 'px';
          });
        }

        const w = canvas.clientWidth;
        const h = canvas.clientHeight;
        if (w > 0 && h > 0 && (canvas.width !== w || canvas.height !== h)) {
          canvas.width = w;
          canvas.height = h;
        }

        if (ctx && canvas.width > 0 && canvas.height > 0) {
          ctx.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
        }

        bitmap.close();
        requestAnimationFrame(() => this.renderFrame());
      })
      .catch(() => requestAnimationFrame(() => this.renderFrame()));
  }

  // ── Hardware buttons ──────────────────────────────────────────────────────────

  pressHome() { this.session.pressKey(3); this._recordKeyAction(Literals.PRESS_HOME); }
  pressBack() { this.session.pressKey(4); this._recordKeyAction(Literals.PRESS_BACK); }
  pressRecents() { this.session.pressKey(187); this._recordKeyAction(Literals.PRESS_RECENTS); }
  pressVolumeUp() { this.session.pressKey(24); this._recordKeyAction(Literals.PRESS_VOLUME_UP); }
  pressVolumeDown() { this.session.pressKey(25); this._recordKeyAction(Literals.PRESS_VOLUME_DOWN); }
  pressPower() { this.session.pressKey(26); }

  private _recordKeyAction(action: string): void {
    if (!this.isRecording) return;
    const id = ++this.actionCounter;
    this.ngZone.run(() => {
      this.actionCaptured.emit({
        id,
        action: action as any,
        element: null,
        coordinates: { x: 0, y: 0 },
        timestamp: new Date().toISOString()
      });
    });
  }

  // ── Type dialog ───────────────────────────────────────────────────────────────
  // The dialog itself now lives in gesture-controls (right panel).
  // device-screen only detects *when* typing is needed and emits the coords.

  private requestType(
    coords: { x: number; y: number; realX: number; realY: number },
    element: RecordedAction['element'] = null
  ): void {
    this.ngZone.run(() => this.typeRequested.emit({ coords, element }));
  }

  // ── Right-click context menu ─────────────────────────────────────────────────

  dismissContextMenu() {
    this.showContextMenu = false;
    this.contextMenuVisible = false;
  }

  async contextMenuInspect(
    action: 'get-text' | 'verify-text' | 'assert-text' | 'verify-text-contains'
  ) {
    const realX = this.contextMenuRealX;
    const realY = this.contextMenuRealY;
    this.dismissContextMenu();

    try {
      const xml = await this.session.fetchPageSource();
      const element = this.session.parseElementFromSource(xml, realX, realY);
      const result = element.text || '(no text found)';

      this.ngZone.run(() => {
        this.contextMenuAction.emit({ action, x: realX, y: realY, result, element });

        if (this.isRecording) {
          const id = ++this.actionCounter;
          this.actionCaptured.emit({
            id,
            action: action as any,
            element,
            coordinates: { x: realX, y: realY },
            value: result,
            timestamp: new Date().toISOString()
          });
        }
      });
    } catch {
      // silently fail
    }
  }

  // ── IME keyboard action menu ──────────────────────────────────────────────────
  showImeMenu = false;
  imeMenuX = 0;
  imeMenuY = 0;

  toggleImeMenu(event: MouseEvent) {
    event.stopPropagation();
    if (this.showImeMenu) { this.showImeMenu = false; return; }
    const btn = event.currentTarget as HTMLElement;
    const rect = btn.getBoundingClientRect();
    this.imeMenuX = rect.right + 8;
    this.imeMenuY = rect.top;
    this.showImeMenu = true;
  }

  closeImeMenu() { this.showImeMenu = false; }

  pressKeyboardAction(
    key: 'search' | 'done' | 'go' | 'send' | 'next' | 'previous' | 'enter'
  ) {
    this.showImeMenu = false;

    const IME_KEYCODES: Record<string, number> = {
      search: 84, done: 66, go: 66, send: 66,
      next: 61, previous: 61, enter: 66,
    };
    this.session.pressKey(IME_KEYCODES[key] || 66);

    if (!this.isRecording) return;
    const id = ++this.actionCounter;
    this.ngZone.run(() => {
      this.actionCaptured.emit({
        id,
        action: Literals.KEYBOARD_ACTION as any,
        element: null,
        coordinates: { x: 0, y: 0 },
        value: key,
        timestamp: new Date().toISOString()
      });
    });
  }

  // ── Canvas event setup ────────────────────────────────────────────────────────

  private setupCanvasEvents(): void {
    const canvas = this.canvasRef.nativeElement;
    const overlay = this.overlayRef.nativeElement;

    const syncCanvasSize = () => {
      const w = canvas.clientWidth;
      const h = canvas.clientHeight;
      if (w > 0 && h > 0) {
        if (canvas.width !== w || canvas.height !== h) { canvas.width = w; canvas.height = h; }
        if (overlay.width !== w || overlay.height !== h) { overlay.width = w; overlay.height = h; }
      }
    };
    syncCanvasSize();
    const ro = new ResizeObserver(syncCanvasSize);
    ro.observe(canvas);

    // ── Overlay drawing helpers ───────────────────────────────────────────────

    const clearOverlay = () => {
      const ctx = overlay.getContext('2d');
      if (ctx) ctx.clearRect(0, 0, overlay.width, overlay.height);
    };

    const scheduleOverlayClear = (ms = 600) => {
      if (this.overlayTimer) clearTimeout(this.overlayTimer);
      this.overlayTimer = setTimeout(() => { clearOverlay(); this.overlayTimer = null; }, ms);
    };

    const drawOverlayTap = (x: number, y: number, color = 'rgba(255,80,80,0.9)', radius = 20) => {
      clearOverlay();
      const ctx = overlay.getContext('2d'); if (!ctx) return;
      ctx.save();
      ctx.beginPath(); ctx.arc(x, y, radius, 0, 2 * Math.PI);
      ctx.strokeStyle = color; ctx.lineWidth = 3; ctx.stroke();
      ctx.beginPath(); ctx.arc(x, y, 5, 0, 2 * Math.PI);
      ctx.fillStyle = color; ctx.fill();
      ctx.restore();
      scheduleOverlayClear();
    };

    const drawOverlayDoubleTap = (x: number, y: number) => {
      clearOverlay();
      const ctx = overlay.getContext('2d'); if (!ctx) return;
      ctx.save();
      ctx.beginPath(); ctx.arc(x, y, 28, 0, 2 * Math.PI);
      ctx.strokeStyle = 'rgba(139,92,246,0.9)'; ctx.lineWidth = 3; ctx.stroke();
      ctx.beginPath(); ctx.arc(x, y, 14, 0, 2 * Math.PI);
      ctx.strokeStyle = 'rgba(139,92,246,0.6)'; ctx.lineWidth = 2; ctx.stroke();
      ctx.restore();
      scheduleOverlayClear();
    };

    const drawOverlayHold = (x: number, y: number) => {
      clearOverlay();
      const ctx = overlay.getContext('2d'); if (!ctx) return;
      ctx.save();
      ctx.beginPath(); ctx.arc(x, y, 30, 0, 2 * Math.PI);
      ctx.strokeStyle = 'rgba(245,158,11,0.9)'; ctx.lineWidth = 4; ctx.stroke();
      ctx.beginPath(); ctx.arc(x, y, 8, 0, 2 * Math.PI);
      ctx.fillStyle = 'rgba(245,158,11,0.8)'; ctx.fill();
      ctx.restore();
      scheduleOverlayClear(800);
    };

    const drawOverlaySwipeStart = (x: number, y: number) => {
      clearOverlay();
      const ctx = overlay.getContext('2d'); if (!ctx) return;
      ctx.save();
      ctx.beginPath(); ctx.arc(x, y, 8, 0, 2 * Math.PI);
      ctx.fillStyle = 'rgba(80,200,255,0.9)'; ctx.fill();
      ctx.strokeStyle = '#fff'; ctx.lineWidth = 2; ctx.stroke();
      ctx.restore();
    };

    const drawOverlaySwipeLine = (x1: number, y1: number, x2: number, y2: number) => {
      clearOverlay();
      const ctx = overlay.getContext('2d'); if (!ctx) return;
      ctx.save();
      ctx.beginPath(); ctx.moveTo(x1, y1); ctx.lineTo(x2, y2);
      ctx.strokeStyle = 'rgba(80,200,255,0.7)'; ctx.lineWidth = 3;
      ctx.setLineDash([6, 4]); ctx.stroke();
      ctx.beginPath(); ctx.arc(x1, y1, 8, 0, 2 * Math.PI);
      ctx.fillStyle = 'rgba(80,200,255,0.9)'; ctx.setLineDash([]); ctx.fill();
      ctx.beginPath(); ctx.arc(x2, y2, 6, 0, 2 * Math.PI);
      ctx.fillStyle = 'rgba(255,255,255,0.7)'; ctx.fill();
      ctx.restore();
    };

    const drawOverlaySwipeFinal = (x1: number, y1: number, x2: number, y2: number) => {
      clearOverlay();
      const ctx = overlay.getContext('2d'); if (!ctx) return;
      ctx.save();
      ctx.beginPath(); ctx.moveTo(x1, y1); ctx.lineTo(x2, y2);
      ctx.strokeStyle = 'rgba(80,200,255,0.9)'; ctx.lineWidth = 3; ctx.stroke();
      const angle = Math.atan2(y2 - y1, x2 - x1);
      ctx.beginPath();
      ctx.moveTo(x2, y2);
      ctx.lineTo(x2 - 15 * Math.cos(angle - 0.4), y2 - 15 * Math.sin(angle - 0.4));
      ctx.lineTo(x2 - 15 * Math.cos(angle + 0.4), y2 - 15 * Math.sin(angle + 0.4));
      ctx.closePath(); ctx.fillStyle = 'rgba(80,200,255,0.9)'; ctx.fill();
      ctx.restore();
      scheduleOverlayClear(600);
    };

    // ── Gesture detection constants ───────────────────────────────────────────

    const HOLD_DELAY = 600;
    const SWIPE_THRESHOLD = 12;
    const DOUBLE_TAP_GAP = 300;
    const DOUBLE_TAP_DIST = 40;

    let pointerDownTime = 0;
    let pointerDownCoords: { x: number; y: number } | null = null;
    let lastTapTime = 0;
    let lastTapCoords: { x: number; y: number } | null = null;
    let swipeInProgress = false;
    let pendingTapTimer: any = null;

    // ── Helper: clear pre-tap snapshot ───────────────────────────────────────
    const clearPreTapSnapshot = () => {
      this.preTapSourceSnapshot = null;
      this.preTapSnapshotCoords = null;
      this.preTapSourcePromise  = null;
    };

    // ── Right-click / contextmenu ─────────────────────────────────────────────
    overlay.addEventListener('contextmenu', (e: MouseEvent) => {
      e.preventDefault();
      if (!this.isConnected) return;
      this.isRightClickPending = true;

      const coords = this.getCoordsFromMouse(e);
      this.ngZone.run(() => {
        this.dismissContextMenu();
        this.contextMenuX = e.clientX;
        this.contextMenuY = e.clientY;
        this.contextMenuRealX = coords.realX;
        this.contextMenuRealY = coords.realY;
        this.contextMenuVisible = false;
        this.showContextMenu = true;

        setTimeout(() => {
          const menuEl = this.ctxMenuRef.nativeElement;
          if (!menuEl) return;

          const menuRect = menuEl.getBoundingClientRect();
          const menuW = menuRect.width;
          const menuH = menuRect.height;
          const screenRect = this.overlayRef.nativeElement.getBoundingClientRect();
          const padding = 6;

          let x = e.clientX;
          if (x + menuW + padding > screenRect.right) x = e.clientX - menuW;
          x = Math.max(screenRect.left + padding, x);

          let y = e.clientY;
          if (y + menuH + padding > window.innerHeight) y = e.clientY - menuH;
          y = Math.max(padding, y);

          this.contextMenuX = x;
          this.contextMenuY = y;
          this.contextMenuVisible = true;
        }, 0);
      });
    });

    // ── Pointer events ────────────────────────────────────────────────────────

    overlay.addEventListener('pointerdown', (e: PointerEvent) => {
      if (!this.isConnected) return;
      if (e.button === 2) { this.isRightClickPending = true; return; }
      this.isRightClickPending = false;
      if (this.showContextMenu) {
        this.ngZone.run(() => this.dismissContextMenu());
        return;
      }
      overlay.setPointerCapture(e.pointerId);

      const coords = this.getCoords(e);
      this.swipeStart = coords;
      this.isLongPress = false;
      swipeInProgress = false;
      pointerDownTime = Date.now();
      pointerDownCoords = { x: e.clientX, y: e.clientY };

      if (this.fingerMode === '1') {
        drawOverlaySwipeStart(coords.x, coords.y);

        // ── PRE-FETCH page source at the moment of touch ──────────────────
        // Fire-and-forget: fetch starts NOW while the screen is stable.
        // By the time the 300ms double-tap timer fires, it will be ready.
        // Solves dropdown options, pickers, bottom sheets — these disappear
        // within ~100ms, well before the 300ms timer would fire.
        clearPreTapSnapshot();
        this.preTapSnapshotCoords = { realX: coords.realX, realY: coords.realY };

        this.preTapSourcePromise = this.session.fetchPageSource()
          .then(xml => {
            // Only store if this touch hasn't turned into a swipe yet
            if (this.preTapSnapshotCoords) {
              this.preTapSourceSnapshot = xml;
            }
            return xml;
          })
          .catch(() => { clearPreTapSnapshot(); return Promise.reject('snapshot failed'); });

        // Long-press detection
        this.longPressTimer = setTimeout(() => {
          if (pointerDownCoords && !swipeInProgress) {
            this.isLongPress = true;
            drawOverlayHold(coords.x, coords.y);
          }
        }, HOLD_DELAY);

      } else if (this.fingerMode === '2') {
        if (this.selectedMode === Literals.TWO_FINGER_SWIPE) drawOverlaySwipeStart(coords.x, coords.y);
      } else if (this.fingerMode === '3') {
        if (this.selectedMode === Literals.THREE_FINGER_SWIPE) drawOverlaySwipeStart(coords.x, coords.y);
      }
    });

    overlay.addEventListener('pointermove', (e: PointerEvent) => {
      if (!this.swipeStart || !pointerDownCoords) return;
      const dx = Math.abs(e.clientX - pointerDownCoords.x);
      const dy = Math.abs(e.clientY - pointerDownCoords.y);
      if (dx > SWIPE_THRESHOLD || dy > SWIPE_THRESHOLD) {
        if (!swipeInProgress) {
          clearTimeout(this.longPressTimer);
          this.longPressTimer = null;
          swipeInProgress = true;
          // Finger moved — this is a swipe, not a tap. Discard snapshot.
          clearPreTapSnapshot();
        }
        const coords = this.getCoords(e);
        drawOverlaySwipeLine(this.swipeStart.x, this.swipeStart.y, coords.x, coords.y);
      }
    });

    overlay.addEventListener('pointerup', (e: PointerEvent) => {
      if (!this.isConnected) return;
      if (this.isRightClickPending) { this.isRightClickPending = false; return; }
      clearTimeout(this.longPressTimer);
      this.longPressTimer = null;

      // ── Long press ────────────────────────────────────────────────────────
      if (this.isLongPress) {
        const holdMs = Math.min(Date.now() - pointerDownTime, 10000);
        const lpCoords = this.swipeStart!;
        this.sendLongPressWithDuration(lpCoords.realX, lpCoords.realY, holdMs);

        if (this.isRecording) {
          this.captureAndRecord(
            Literals.LONG_PRESS,
            lpCoords.x, lpCoords.y,
            lpCoords.realX, lpCoords.realY,
            undefined,
            `${holdMs}ms`
          );
        }

        this.isLongPress = false;
        this.swipeStart = null;
        pointerDownCoords = null;
        swipeInProgress = false;
        clearPreTapSnapshot();
        return;
      }

      const coords = this.getCoords(e);
      if (!this.swipeStart) return;

      const dx = Math.abs(coords.x - this.swipeStart.x);
      const dy = Math.abs(coords.y - this.swipeStart.y);

      if (this.fingerMode === '1') {

        // ── Explicit type mode ──────────────────────────────────────────────
        if (this.selectedMode === Literals.TYPE && !swipeInProgress) {
          drawOverlayTap(coords.x, coords.y);
          const { x: tx, y: ty, realX: trx, realY: try_ } = coords;
          this.session.sendAction('tap', coords.realX, coords.realY);
          this.requestType(coords, null);
          this.swipeStart = null;
          pointerDownCoords = null;
          swipeInProgress = false;
          if (this.isConnected) {
            this.ngZone.run(() => this.locatorFetchingChange.emit(true));
            const snapshot        = this.preTapSourceSnapshot;
            const inflightPromise = this.preTapSourcePromise;
            clearPreTapSnapshot();
            const sourcePromise: Promise<string> = snapshot
              ? Promise.resolve(snapshot)
              : inflightPromise || this.session.fetchPageSource();

            sourcePromise
              .then(xml => {
                const el = this.session.parseElementFromSource(xml, trx, try_);
                this.session.sendAction('tap', trx, try_);
                this.ngZone.run(() => this.locatorFetchingChange.emit(false));
                this.requestType({ x: tx, y: ty, realX: trx, realY: try_ }, el);
              })
              .catch(() => {
                this.session.sendAction('tap', trx, try_);
                this.ngZone.run(() => this.locatorFetchingChange.emit(false));
                this.requestType({ x: tx, y: ty, realX: trx, realY: try_ }, null);
              });
          } else {
            clearPreTapSnapshot();
            this.session.sendAction('tap', trx, try_);
            this.requestType({ x: tx, y: ty, realX: trx, realY: try_ }, null);
          }
          return;
        }

        // ── Slider recording mode ─────────────────────────────────────────────
        if (this.isRecordingSlider && this.sliderBounds && swipeInProgress) {
          console.log("slider")
          const b = this.sliderBounds;
          const isHorizontal = (b.x2 - b.x1) > (b.y2 - b.y1);
          let fromPct: number;
          let toPct: number;

          if (isHorizontal) {
            fromPct = Math.max(0, Math.min(1, (this.swipeStart!.realX - b.x1) / (b.x2 - b.x1)));
            toPct   = Math.max(0, Math.min(1, (coords.realX          - b.x1) / (b.x2 - b.x1)));
          } else {
            fromPct = Math.max(0, Math.min(1, (this.swipeStart!.realY - b.y1) / (b.y2 - b.y1)));
            toPct   = Math.max(0, Math.min(1, (coords.realY          - b.y1) / (b.y2 - b.y1)));
          }

          drawOverlaySwipeFinal(this.swipeStart!.x, this.swipeStart!.y, coords.x, coords.y);
          this.session.sendSwipe(
            this.swipeStart!.realX, this.swipeStart!.realY,
            coords.realX, coords.realY
          );

          if (this.isRecording) {
            const id = ++this.actionCounter;
            this.ngZone.run(() => {
              this.actionCaptured.emit({
                id,
                action: 'slider' as any,
                element: this.sliderElement,
                coordinates: { x: this.swipeStart!.realX, y: this.swipeStart!.realY },
                swipeTo:      { x: coords.realX, y: coords.realY },
                sliderPercent: { fromPct, toPct, axis: isHorizontal ? 'x' : 'y' },
                timestamp: new Date().toISOString()
              });
            });
          }

          // Exit slider mode after one swipe
          this.isRecordingSlider = false;
          this.sliderElement     = null;
          this.sliderBounds      = null;
          this.swipeStart        = null;
          pointerDownCoords      = null;
          swipeInProgress        = false;
          return;
        
        }

        // ── Swipe ──────────────────────────────────────────────────────────
        if (swipeInProgress && (dx > SWIPE_THRESHOLD || dy > SWIPE_THRESHOLD)) {
          drawOverlaySwipeFinal(this.swipeStart.x, this.swipeStart.y, coords.x, coords.y);
          this.session.sendSwipe(
            this.swipeStart.realX, this.swipeStart.realY,
            coords.realX, coords.realY
          );
          if (this.isRecording) {
            this.captureAndRecord(Literals.SWIPE,
              this.swipeStart.x, this.swipeStart.y,
              this.swipeStart.realX, this.swipeStart.realY,
              coords
            );
          }

        } else if (swipeInProgress) {
          // Did not travel far enough — swallow gesture
          clearOverlay();

        } else {
          // ── Double-tap / single-tap detection ────────────────────────────
          const now = Date.now();
          const timeSinceLastTap = now - lastTapTime;
          const distFromLastTap = lastTapCoords
            ? Math.hypot(coords.x - lastTapCoords.x, coords.y - lastTapCoords.y)
            : Infinity;

          if (timeSinceLastTap < DOUBLE_TAP_GAP && distFromLastTap < DOUBLE_TAP_DIST) {
            // ── Double tap ──────────────────────────────────────────────────
            if (pendingTapTimer) { clearTimeout(pendingTapTimer); pendingTapTimer = null; }
            clearPreTapSnapshot();
            drawOverlayDoubleTap(coords.x, coords.y);
            this.session.sendDoubleTap(coords.realX, coords.realY);
            if (this.isRecording) {
              this.captureAndRecord(Literals.DOUBLE_TAP, coords.x, coords.y, coords.realX, coords.realY);
            }
            lastTapTime = 0;
            lastTapCoords = null;

          } else {
            // ── Single tap (pending 300ms to rule out double tap) ───────────
            if (pendingTapTimer) { clearTimeout(pendingTapTimer); pendingTapTimer = null; }

            const { x: tx, y: ty, realX: trx, realY: try_ } = coords;
            lastTapTime = now;
            lastTapCoords = { x: tx, y: ty };
            drawOverlayTap(tx, ty);

            // Capture snapshot reference NOW — it may already be ready from pointerdown
            const snapshotAtTapTime = this.preTapSourceSnapshot;

            pendingTapTimer = setTimeout(() => {
              pendingTapTimer = null;

              if (this.isConnected) {
                this.ngZone.run(() => this.locatorFetchingChange.emit(true));

                // Use pre-tap snapshot if available — critical for dropdowns/pickers.
                // The snapshot was fetched at pointerdown while the screen was stable.
                // By the time this 300ms timer fires the dropdown may already be closed.
                // Fall back to a fresh fetch if the snapshot didn't arrive in time.
                const snapshot = snapshotAtTapTime || this.preTapSourceSnapshot || null;
                clearPreTapSnapshot();

                const sourcePromise: Promise<string> = snapshot
                  ? Promise.resolve(snapshot)
                  : this.session.fetchPageSource();

                sourcePromise
                  .then(xml => {
                    const el = this.session.parseElementFromSource(xml, trx, try_);
                    const isInput = this.session.isInputElement(el);

                    // Send the tap AFTER locator is captured
                    this.session.sendAction('tap', trx, try_);

                    // ALWAYS record the tap — even for input fields.
                    // Dropdown triggers are EditText nodes but should be recorded
                    // as taps, not swallowed by the type panel.
                    if (this.isRecording) {
                      const id = ++this.actionCounter;
                      this.ngZone.run(() => {
                        this.locatorFetchingChange.emit(false);
                        this.actionCaptured.emit({
                          id, action: 'tap', element: el,
                          coordinates: { x: trx, y: try_ },
                          timestamp: new Date().toISOString()
                        });
                      });
                    } else {
                      this.ngZone.run(() => this.locatorFetchingChange.emit(false));
                    }

                    // Open type panel if this is a real input field.
                    // Type action will be recorded separately when user confirms text.
                    if (isInput) {
                      this.requestType({ x: tx, y: ty, realX: trx, realY: try_ }, el);
                    }
                  })
                  .catch(() => {
                    // Source fetch failed — send tap and record coordinate-only
                    this.session.sendAction('tap', trx, try_);
                    if (this.isRecording) {
                      this.captureAndRecord('tap', tx, ty, trx, try_);
                    }
                    this.ngZone.run(() => this.locatorFetchingChange.emit(false));
                  });

              } else {
                // Disconnected — send tap directly
                this.session.sendAction('tap', trx, try_);
                if (this.isRecording) {
                  this.captureAndRecord('tap', tx, ty, trx, try_);
                }
              }
            }, DOUBLE_TAP_GAP);
          }
        }

      } else if (this.fingerMode === '2') {
        if (this.selectedMode === Literals.ZOOM_IN || this.selectedMode === Literals.ZOOM_OUT) {
          drawOverlayTap(coords.x, coords.y, 'rgba(249,115,22,0.9)', 24);
          const pinch = this.selectedMode === Literals.ZOOM_IN ? 0 : 1;
          this.session.sendPinch(coords.realX, coords.realY, 200, pinch);
          if (this.isRecording) {
            this.captureAndRecord(this.selectedMode, coords.x, coords.y, coords.realX, coords.realY);
          }
        } else if (this.selectedMode === Literals.TWO_FINGER_SWIPE) {
          if (dx > SWIPE_THRESHOLD || dy > SWIPE_THRESHOLD) {
            drawOverlaySwipeFinal(this.swipeStart.x, this.swipeStart.y, coords.x, coords.y);
            this.session.sendTwoFingerSwipe(
              this.swipeStart.realX, this.swipeStart.realY,
              coords.realX, coords.realY
            );
            if (this.isRecording) {
              this.captureAndRecord(Literals.TWO_FINGER_SWIPE,
                this.swipeStart.x, this.swipeStart.y,
                this.swipeStart.realX, this.swipeStart.realY,
                coords
              );
            }
          } else {
            clearOverlay();
          }
        }

      } else if (this.fingerMode === '3') {
        if (this.selectedMode === Literals.THREE_FINGER_SWIPE) {
          const dxCss = Math.abs(coords.x - this.swipeStart.x);
          const dyCss = Math.abs(coords.y - this.swipeStart.y);

          if (dxCss > SWIPE_THRESHOLD || dyCss > SWIPE_THRESHOLD) {
            drawOverlaySwipeFinal(this.swipeStart.x, this.swipeStart.y, coords.x, coords.y);
            this.session.sendThreeFingerSwipe(
              this.swipeStart.realX, this.swipeStart.realY,
              coords.realX, coords.realY
            );
            if (this.isRecording) {
              this.captureAndRecord(Literals.THREE_FINGER_SWIPE,
                this.swipeStart.x, this.swipeStart.y,
                this.swipeStart.realX, this.swipeStart.realY,
                coords
              );
            }
          } else {
            clearOverlay();
          }
        }
      }

      this.swipeStart = null;
      pointerDownCoords = null;
      swipeInProgress = false;
    });

    overlay.addEventListener('pointercancel', () => {
      clearTimeout(this.longPressTimer);
      if (pendingTapTimer) { clearTimeout(pendingTapTimer); pendingTapTimer = null; }
      this.longPressTimer = null;
      pointerDownCoords = null;
      this.swipeStart = null;
      this.isLongPress = false;
      swipeInProgress = false;
      clearOverlay();
    });
  }

  // ── Coordinate mapping ────────────────────────────────────────────────────────

  private getCoords(e: PointerEvent) {
    const canvas = this.canvasRef.nativeElement;
    const rect = canvas.getBoundingClientRect();
    const cssX = e.clientX - rect.left;
    const cssY = e.clientY - rect.top;
    const scaleX = this.session.imageWidth / rect.width;
    const scaleY = this.session.imageHeight / rect.height;
    return {
      x: cssX, y: cssY,
      realX: Math.round(cssX * scaleX),
      realY: Math.round(cssY * scaleY)
    };
  }

  private getCoordsFromMouse(e: MouseEvent) {
    const canvas = this.canvasRef.nativeElement;
    const rect = canvas.getBoundingClientRect();
    const cssX = e.clientX - rect.left;
    const cssY = e.clientY - rect.top;
    const scaleX = this.session.imageWidth / rect.width;
    const scaleY = this.session.imageHeight / rect.height;
    return {
      x: cssX, y: cssY,
      realX: Math.round(cssX * scaleX),
      realY: Math.round(cssY * scaleY)
    };
  }

  // ── Action recording ──────────────────────────────────────────────────────────

  private captureAndRecord(
    action: GestureMode,
    _cx: number, _cy: number,
    realX: number, realY: number,
    swipeTo?: { x: number; y: number; realX: number; realY: number },
    value?: string
  ): void {
    const id = ++this.actionCounter;
    const record: RecordedAction = {
      id, action, element: null,
      coordinates: { x: realX, y: realY },
      timestamp: new Date().toISOString(),
      ...(value ? { value } : {}),
      ...(swipeTo ? { swipeTo: { x: swipeTo.realX, y: swipeTo.realY } } : {})
    };

    // Coordinate-only actions — no element locator needed
    const isCoordOnly = action === Literals.SWIPE
      || action === Literals.TWO_FINGER_SWIPE
      || action === Literals.THREE_FINGER_SWIPE
      || action === Literals.ZOOM_IN
      || action === Literals.ZOOM_OUT;

    if (isCoordOnly) {
      this.ngZone.run(() => {
        this.actionCaptured.emit(record);
        this.locatorFetchingChange.emit(false);
      });
      return;
    }

    this.ngZone.run(() => this.locatorFetchingChange.emit(true));

    this.session.fetchPageSource()
      .then(xml => {
        const element = this.session.parseElementFromSource(xml, realX, realY);
        this.ngZone.run(() => {
          this.actionCaptured.emit({ ...record, element });
          this.locatorFetchingChange.emit(false);
        });
      })
      .catch(() => {
        this.ngZone.run(() => {
          this.actionCaptured.emit(record);
          this.locatorFetchingChange.emit(false);
        });
      });
  }

  // ── Long-press with duration ──────────────────────────────────────────────────
  // Sends the longpress via the existing session socket if it supports a duration
  // parameter, otherwise falls back to the standard sendAction call.
  // The holdMs value is also stored in the recorded action's value field.
  private sendLongPressWithDuration(realX: number, realY: number, holdMs: number): void {
    if (typeof (this.session as any).sendLongPress === 'function') {
      (this.session as any).sendLongPress(realX, realY, holdMs);
    } else {
      this.session.sendAction('longpress', realX, realY);
    }
  }

  async contextMenuRecordSlider() {
    const realX = this.contextMenuRealX;
    const realY = this.contextMenuRealY;
    this.dismissContextMenu();

    try {
      const xml     = await this.session.fetchPageSource();
      const element = this.session.parseElementFromSource(xml, realX, realY);
      const bounds  = this.session.parseBoundsFromSource(xml, realX, realY);

      if (!bounds) {
        console.warn('No bounds found for slider element');
        return;
      }

      this.sliderElement    = element;
      this.sliderBounds     = bounds;
      this.isRecordingSlider = true;

      this.ngZone.run(() => {
        // Show visual indicator that slider recording is active
      });
    } catch {
      console.warn('Failed to fetch slider element');
    }
  }

  highlightElement(action: RecordedAction, xml: string | null): void {
    const overlay = this.overlayRef.nativeElement;
    const ctx     = overlay.getContext('2d');
    if (!ctx) return;

    if (this.overlayTimer) { clearTimeout(this.overlayTimer); this.overlayTimer = null; }
    ctx.clearRect(0, 0, overlay.width, overlay.height);

    if (!xml || !action.element.xpaths) return;

    const xpaths = action.element.xpaths;
    const candidates: Array<{ label: string; xpath: string }> = [];
    if (xpaths.contentDescXpath)  candidates.push({ label: 'a11y-xpath',  xpath: xpaths.contentDescXpath });
    if (xpaths.relativeXpath)     candidates.push({ label: 'relative',    xpath: xpaths.relativeXpath });
    if (xpaths.textBasedXpath)    candidates.push({ label: 'text-xpath',  xpath: xpaths.textBasedXpath });
    if (xpaths.resourceIdXpath)   candidates.push({ label: 'res-xpath',   xpath: xpaths.resourceIdXpath });
    if (xpaths.absoluteXpath)     candidates.push({ label: 'absolute',    xpath: xpaths.absoluteXpath });

    const doc = new DOMParser().parseFromString(xml, 'text/xml');

    let matchedNode:   Element | null = null;
    let matchedLabel = '';

    for (const { label, xpath } of candidates) {
      const node = this._evalXPath(doc, xpath);
      if (node) { matchedNode = node; matchedLabel = label; break; }
    }

    if (!matchedNode) return; // XPath not found on current screen — draw nothing

    // ── Parse bounds from the matched node ───────────────────────────────────
    const rawBounds = matchedNode.getAttribute('bounds');
    const bm        = rawBounds.match(/\[(\d+),(\d+)\]\[(\d+),(\d+)\]/);
    if (!bm) return;

    const scaleX = overlay.width  / (this.session.imageWidth  || overlay.width);
    const scaleY = overlay.height / (this.session.imageHeight || overlay.height);

    const x = +bm[1] * scaleX;
    const y = +bm[2] * scaleY;
    const w = (+bm[3] - +bm[1]) * scaleX;
    const h = (+bm[4] - +bm[2]) * scaleY;

    ctx.save();

    // Fill
    ctx.fillStyle = 'rgba(108, 99, 255, 0.18)';
    ctx.fillRect(x, y, w, h);

    // Border with glow
    ctx.shadowColor = '#6c63ff';
    ctx.shadowBlur  = 8;
    ctx.strokeStyle = '#6c63ff';
    ctx.lineWidth   = 2;
    ctx.strokeRect(x, y, w, h);
    ctx.shadowBlur  = 0;

    // ── Badge showing which XPath matched ────────────────────────────────────
    // const badgeText = matchedLabel;
    const badgeText = '';
    ctx.font = 'bold 10px monospace';
    // const tw  = ctx.measureText(badgeText).width + 10;
    const tw  = ctx.measureText(badgeText).width;
    const bx  = x;
    const by  = y > 20 ? y - 20 : y + h + 4;
    ctx.fillStyle = '#6c63ff';
    ctx.fillRect(bx, by, tw, 17);
    ctx.fillStyle = '#ffffff';
    ctx.fillText(badgeText, bx + 5, by + 12);

    ctx.restore();

    // Auto-clear after 3 s
    this.overlayTimer = setTimeout(() => {
      ctx.clearRect(0, 0, overlay.width, overlay.height);
      this.overlayTimer = null;
    }, 3000);
  }

  private _evalXPath(doc: Document, xpath: string): Element | null {
    try {
      const result = doc.evaluate(xpath, doc, null, XPathResult.FIRST_ORDERED_NODE_TYPE, null);
      return result.singleNodeValue as Element | null;
    } catch {
      return null;
    }
  }

}